import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress
} from '@mui/material';
import { Edit, Delete, Add, Person, Business } from '@mui/icons-material';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types';

interface ProjectAssignment {
  id: string;
  userId: string;
  projectId: string;
  role: string;
  isActive: boolean;
  assignedAt: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  project?: {
    id: string;
    name: string;
    key: string;
  };
}

const ProjectAssignments: React.FC = () => {
  const [searchParams] = useSearchParams();
  const projectIdFromUrl = searchParams.get('projectId');
  
  const [assignments, setAssignments] = useState<ProjectAssignment[]>([]);
  const { user } = useAuth();
  const canManage = !!user && [UserRole.MANAGER, UserRole.SUPER_MANAGER].includes(user.role);
  const canEditRole = !!user && user.role === UserRole.SUPER_MANAGER;
  const [employees, setEmployees] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<ProjectAssignment | null>(null);
  const [formData, setFormData] = useState({
    userId: '',
    projectId: projectIdFromUrl || '',
    role: 'developer'
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const roles = [
    'developer',
    'tester',
    'designer',
    'analyst',
    'manager',
    'lead',
    'architect',
    'devops',
    'qa',
    'ui_ux'
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [employeesData, projectsData] = await Promise.all([
        apiService.getAvailableEmployees(),
        apiService.getAvailableProjects()
      ]);
      setEmployees(employeesData);
      setProjects(projectsData);
      
      // Load assignments for specific project if projectId is provided, otherwise load all assignments
      if (projectIdFromUrl) {
        try {
          console.log('Loading assignments for project:', projectIdFromUrl);
          const projectAssignments = await apiService.getProjectAssignments(projectIdFromUrl);
          console.log('Project assignments loaded:', projectAssignments);
          setAssignments(projectAssignments);
        } catch (error) {
          console.error('Error loading project assignments:', error);
        }
      } else {
        try {
          // Load all assignments when no specific project is selected
          console.log('Loading all assignments');
          const allAssignments = await apiService.getAllProjectAssignments();
          console.log('All assignments loaded:', allAssignments);
          setAssignments(allAssignments);
        } catch (error) {
          console.error('Error loading all assignments:', error);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (assignment?: ProjectAssignment) => {
    if (assignment) {
      setSelectedAssignment(assignment);
      setFormData({
        userId: assignment.userId,
        projectId: assignment.projectId,
        role: assignment.role
      });
    } else {
      setSelectedAssignment(null);
      setFormData({
        userId: '',
        projectId: '',
        role: 'developer'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedAssignment(null);
    setFormData({
      userId: '',
      projectId: '',
      role: 'developer'
    });
  };

  const handleSubmit = async () => {
    if (!formData.userId || !formData.projectId || !formData.role) {
      setError('Please fill in all fields');
      return;
    }

    try {
      if (selectedAssignment) {
        await apiService.updateEmployeeRole(
          formData.userId,
          formData.projectId,
          formData.role
        );
        setSuccess('Employee role updated successfully');
      } else {
        await apiService.assignEmployeeToProject(
          formData.userId,
          formData.projectId,
          formData.role
        );
        setSuccess('Employee assigned to project successfully');
      }
      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Error saving assignment:', error);
      setError('Failed to save assignment');
    }
  };

  const handleRemoveAssignment = async (userId: string, projectId: string) => {
    if (window.confirm('Are you sure you want to remove this employee from the project?')) {
      try {
        await apiService.removeEmployeeFromProject(userId, projectId);
        setSuccess('Employee removed from project successfully');
        loadData();
      } catch (error) {
        console.error('Error removing assignment:', error);
        setError('Failed to remove assignment');
      }
    }
  };

  const getEmployeeName = (userId: string) => {
    const employee = employees.find(emp => emp.id === userId);
    return employee ? `${employee.firstName} ${employee.lastName}` : 'Unknown';
  };

  const getProjectName = (projectId: string) => {
    const project = projects.find(proj => proj.id === projectId);
    return project ? project.name : 'Unknown';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Project Assignments
        </Typography>
        {canManage && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Assign Employee
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Current Assignments
              </Typography>
              {assignments.length === 0 ? (
                <Typography color="textSecondary">
                  No assignments found. Click "Assign Employee" to add employees to projects.
                  <br />
                  <small>Debug: assignments array length is {assignments.length}</small>
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee</TableCell>
                        <TableCell>Project</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Assigned Date</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assignments.map((assignment) => (
                        <TableRow key={assignment.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Person sx={{ mr: 1 }} />
                              {getEmployeeName(assignment.userId)}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Business sx={{ mr: 1 }} />
                              {getProjectName(assignment.projectId)}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={assignment.role.replace('_', ' ')} 
                              color="primary" 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(assignment.assignedAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {canEditRole && (
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(assignment)}
                                title="Edit Role"
                              >
                                <Edit />
                              </IconButton>
                            )}
                            {canManage && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveAssignment(assignment.userId, assignment.projectId)}
                                title="Remove from Project"
                              >
                                <Delete />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Assignment Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedAssignment ? 'Edit Assignment' : 'Assign Employee to Project'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Employee</InputLabel>
              <Select
                value={formData.userId}
                onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                label="Employee"
              >
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {`${employee.firstName} ${employee.lastName}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Project</InputLabel>
              <Select
                value={formData.projectId}
                onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
                label="Project"
              >
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                label="Role"
              >
                {roles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role.replace('_', ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedAssignment ? 'Update' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectAssignments; 